import { useRef, useState, type FormEvent } from 'react';
import { api, type Tenant } from './api.ts';
import { copy, copyOutcomeText } from './clipboard.ts';
import { message } from './format.ts';

/**
 * Adding one client, and the consent-link reveal that follows it.
 *
 * Two screens did this — Onboarding, where it was the first thing an MSP did,
 * and Admin → Clients, where it opened from a toolbar button — and both are the
 * Clients screen now. It stays one component with two shapes, because it is one
 * operation: a second implementation would be a second place for the consent
 * link to be explained, and how that link reaches the client's Global
 * Administrator is the only part of onboarding DriftWatch cannot do itself.
 *
 * The call sites differ in chrome, not in behaviour: a dismissible drawer, or a
 * panel left on the screen. That is `onCancel` being supplied or not, plus the
 * heading level the surrounding document needs.
 *
 * **The form owns its own outcome.** It used to hand the create to a runner the
 * screen supplied, which put the refusal in a banner at the top of the page —
 * several hundred pixels above the button, with the lead paragraph, a callout
 * and a row of stat tiles in between. An operator
 * pressed Create client and saw nothing happen. A message about a button belongs
 * beside that button, so the call, its failure text and its success live here;
 * the screen is told only that a client now exists so it can reload its list.
 */

/**
 * The runner a screen hands down: it performs the mutation, reloads whatever
 * that screen lists, and announces the outcome, returning whether it worked so
 * a form can keep its fields on a rejection rather than throwing away what the
 * operator had typed. Named once here rather than restated at every prop site.
 *
 * Still the contract for the row drawers on Clients, whose messages are about a
 * row in a list rather than about a form.
 */
export type RunAction = (action: () => Promise<void>, said: string) => Promise<boolean>;

interface AddClientProps {
  /**
   * Whether this deployment has an Entra application. Without one the server
   * refuses `POST /tenants` outright — a client is created together with its
   * consent link, and there is nothing to mint a link from — so the fields are
   * replaced by the reason rather than left to be filled in and rejected.
   */
  readonly entraConfigured: boolean;
  /** The client now exists; the screen reloads whatever it lists. */
  readonly onCreated: (tenant: Tenant) => void;
  /** The screen's live region. The inline messages here are visual only. */
  readonly announce: (text: string) => void;
  /** Omit on a screen that shows the form permanently; the dismiss button goes with it. */
  readonly onCancel?: () => void;
  /** `section` where the form is a top-level block on the page, `drawer` inside one. */
  readonly variant?: 'section' | 'drawer';
}

export const AddClient = ({
  entraConfigured,
  onCreated,
  announce,
  onCancel,
  variant = 'drawer',
}: AddClientProps) => {
  const [entraTenantId, setEntraTenantId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [consentUrl, setConsentUrl] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A permanent block on a page is a named region and a page-level heading; the
  // same form inside a drawer is neither, and announcing it as one would put a
  // landmark inside a table row.
  const section = variant === 'section';
  const Wrapper = section ? 'section' : 'div';
  const Heading = section ? 'h2' : 'h3';
  const headingId = section ? 'add-client-heading' : undefined;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setFailure(null);
    // A link shown for the previous client must not outlive an attempt that
    // produced no link: the two would read as one another's outcome.
    setConsentUrl(null);
    try {
      const name = displayName.trim();
      const created = await api.createTenant(entraTenantId.trim(), name);
      setConsentUrl(created.consentUrl);
      announce(`${name} was created. Send its consent link to the client’s Global Administrator.`);
      onCreated(created.tenant);
    } catch (cause) {
      // The fields keep what was typed: a rejected tenant id is usually one
      // character wrong, and clearing the form makes the operator start again.
      const text = message(cause);
      setFailure(text);
      announce(`That did not happen. ${text}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrapper className="card add-client" aria-labelledby={headingId}>
      <div className="card-head">
        <Heading id={headingId}>Add a client</Heading>
      </div>
      <div className="card-body">
        {entraConfigured ? (
          <form onSubmit={(event) => void submit(event)} noValidate>
            <div className="field">
              <label htmlFor="new-client-name">Client name</label>
              <input
                id="new-client-name"
                type="text"
                autoComplete="off"
                value={displayName}
                disabled={busy}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="new-client-tenant">Microsoft tenant id</label>
              <input
                id="new-client-tenant"
                type="text"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                className="mono"
                placeholder="a1b2c3d4-0000-0000-0000-000000000001"
                value={entraTenantId}
                disabled={busy}
                onChange={(event) => setEntraTenantId(event.target.value)}
              />
              <span className="hint">
                The directory (tenant) id from the client’s Entra admin centre. It is validated on
                the server, which will say if it is not a tenant id.
              </span>
            </div>

            <div className="toolbar">
              <button
                type="submit"
                className="btn btn-primary"
                aria-busy={busy || undefined}
                disabled={busy}
              >
                {busy ? 'Creating…' : 'Create client'}
              </button>
              {onCancel !== undefined && (
                <button type="button" className="btn btn-quiet" disabled={busy} onClick={onCancel}>
                  {consentUrl === null ? 'Cancel' : 'Close'}
                </button>
              )}
            </div>

            {failure !== null && <p className="error add-client-error">{failure}</p>}
          </form>
        ) : (
          <>
            <div className="callout">
              <span className="callout-title">
                Clients cannot be added until an Entra application is registered
              </span>
              Every client is created together with a consent link for their Global Administrator to
              open, and that link is issued by this deployment&rsquo;s own Entra application. Until
              one is registered and its details are in the server environment, there is nothing to
              issue a link from. Whoever administers this deployment can register it — the procedure
              is in <span className="mono">docs/ONBOARDING.md</span>.
            </div>
            {onCancel !== undefined && (
              <div className="toolbar">
                <button type="button" className="btn btn-quiet" onClick={onCancel}>
                  Close
                </button>
              </div>
            )}
          </>
        )}

        {consentUrl !== null && (
          <ConsentLink
            url={consentUrl}
            what="Consent link for the new client"
            note="Give this to the client’s Global Administrator. Opening it is how they grant DriftWatch its read-only consent."
          />
        )}
      </div>
    </Wrapper>
  );
};

interface ConsentLinkProps {
  readonly url: string;
  readonly what: string;
  readonly note: string;
}

/**
 * The revealed consent link. Shared with the reconnect drawer, which produces
 * the same link for a client whose connection has broken — the same value, the
 * same copy affordance and the same fallback wording when the clipboard is
 * unavailable.
 */
export const ConsentLink = ({ url, what, note }: ConsentLinkProps) => {
  const linkRef = useRef<HTMLInputElement>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const onCopy = async () => {
    setOutcome(copyOutcomeText(await copy(url, linkRef.current), what));
  };

  return (
    <div className="consent-link">
      <span className="label">Consent link</span>
      <div className="toolbar consent-link-row">
        <input
          ref={linkRef}
          type="text"
          className="mono consent-link-url"
          readOnly
          value={url}
          onFocus={(event) => event.target.select()}
        />
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => void onCopy()}>
          Copy
        </button>
      </div>
      <p className="hint">{note}</p>
      {outcome !== null && (
        <p className="status-inline" role="status">
          {outcome}
        </p>
      )}
    </div>
  );
};
